'use client';

import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import type { StepProps } from '@/components/wizard/StepProps';

import { StepContainer } from '../../wizard/StepContainer';
import type { GoogleWorkspaceRestoreState } from '../restoreGoogleWorkspaceWizard';

export const WorkspaceRestoreConfiguration = (
  props: StepProps<GoogleWorkspaceRestoreState>
) => {
  const { wizardState, setWizardState, stepperLabels, back, finish } = props;
  const [isRestoring, setIsRestoring] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const targetPath = wizardState.files[0]?.path ?? '';
  const fileName = targetPath.split('/').filter(Boolean).pop() ?? '';

  const onRestoreClick = async () => {
    setError(undefined);
    setIsRestoring(true);

    try {
      await finish(
        `Started restore job for ${wizardState.files.map((x) => x.path).join(', ')} to ${wizardState.targetWorkspace?.resourceName}`
      );
    } catch {
      setError('Failed to start restore job.');
      setIsRestoring(false);
    }
  };

  return (
    <StepContainer
      stepperLabels={stepperLabels}
      stepperIndex={1}
      nextButtonText='Restore'
      nextButtonLoadingText='Restoring...'
      onBackClick={back}
      canGoNext={() => {
        if (isRestoring) return false;
        if (wizardState.restoreMode === 'rename') {
          return wizardState.newName.trim().length > 0;
        }
        return true;
      }}
      onNextClick={onRestoreClick}
      navigationComponent={
        error && (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        )
      }
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={wizardState.snapshot?.pointInTime}
          vaultId={wizardState.snapshot?.vaultId}
        />
      }
    >
      <Stack gap='24px'>
        <Stack gap='8px'>
          <Typography variant='subtitle1'>Target Workspace</Typography>
          <Typography variant='body2' color='text.secondary'>
            {wizardState.targetWorkspace?.resourceName} (
            {wizardState.targetWorkspace?.tags?.Domain})
          </Typography>
        </Stack>

        <Stack gap='8px'>
          <Typography variant='subtitle1'>Restoring</Typography>
          <Typography variant='body2' color='text.secondary'>
            {targetPath}
          </Typography>
        </Stack>

        <FormControl>
          <FormLabel>
            <Typography variant='subtitle1'>Restore Mode</Typography>
          </FormLabel>
          <RadioGroup
            value={wizardState.restoreMode}
            onChange={(e) =>
              setWizardState((state) => ({
                ...state,
                restoreMode: e.target.value as 'overwrite' | 'rename',
                newName:
                  e.target.value === 'rename' && !state.newName
                    ? `${fileName} (restored)`
                    : state.newName,
              }))
            }
          >
            <FormControlLabel
              value='overwrite'
              control={<Radio />}
              label={
                <Stack>
                  <Typography variant='body1'>
                    Override existing content
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Replace the current content at the original location
                  </Typography>
                </Stack>
              }
              sx={{ alignItems: 'flex-start', marginTop: '8px' }}
            />
            <FormControlLabel
              value='rename'
              control={<Radio />}
              label={
                <Stack>
                  <Typography variant='body1'>
                    Restore with a new name
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Keep the existing content and restore to a new location
                  </Typography>
                </Stack>
              }
              sx={{ alignItems: 'flex-start', marginTop: '8px' }}
            />
          </RadioGroup>
        </FormControl>

        {wizardState.restoreMode === 'rename' && (
          <FormControl size='small'>
            <FormLabel>
              <Typography variant='body2'>New name</Typography>
            </FormLabel>
            <TextField
              size='small'
              value={wizardState.newName}
              onChange={(e) =>
                setWizardState((state) => ({
                  ...state,
                  newName: e.target.value,
                }))
              }
              placeholder={`${fileName} (restored)`}
              sx={{ marginTop: '8px' }}
            />
          </FormControl>
        )}
      </Stack>
    </StepContainer>
  );
};
